const STORAGE_KEY = 'admin_inbox_notifications';
const MAX_STORED = 50;

/** @typedef {{ id: string, from: string, content: string, read: boolean, createdAt: string, icon?: string, data?: Record<string, string> }} InboxNotification */

const listeners = new Set();

export const onInboxNotification = (handler) => {
  listeners.add(handler);
  return () => listeners.delete(handler);
};

export const emitInboxNotification = (notification) => {
  listeners.forEach((handler) => handler(notification));
};

export const normalizeFcmPayload = (payload) => {
  const notification = payload?.notification || {};
  const data = payload?.data || {};
  const title = notification.title || data.title || data.subject || 'Sanduk Admin';
  const body = notification.body || data.body || data.message || data.content || '';
  const id =
    payload?.messageId
    || data.id
    || data.notification_id
    || `fcm-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

  return {
    id: String(id),
    from: String(title),
    content: String(body),
    read: false,
    createdAt: data.created_at || new Date().toISOString(),
    icon: notification.icon || data.icon,
    data: typeof data === 'object' ? data : {},
  };
};

/** @param {unknown} item */
export const normalizeApiNotification = (item) => {
  if (!item || typeof item !== 'object') return null;
  const row = /** @type {Record<string, unknown>} */ (item);
  const id = row.id ?? row.notification_id ?? row._id;
  const from = row.title ?? row.subject ?? row.from ?? row.sender ?? row.type ?? 'Notification';
  const content = row.message ?? row.body ?? row.content ?? row.text ?? '';
  if (!id && !content && !from) return null;

  return {
    id: String(id ?? `api-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`),
    from: String(from),
    content: String(content),
    read: Boolean(row.read ?? row.is_read ?? row.read_at),
    createdAt: String(row.created_at ?? row.createdAt ?? new Date().toISOString()),
    icon: row.icon ? String(row.icon) : undefined,
    data: row.data && typeof row.data === 'object' ? row.data : {},
  };
};

export const parseUnreadCount = (response) => {
  if (!response) return 0;
  const raw =
    response?.data?.count
    ?? response?.data?.unread_count
    ?? response?.count
    ?? response?.unread_count;
  const n = Number(raw);
  return Number.isFinite(n) ? Math.max(0, n) : 0;
};

export const parseNotificationsList = (response) => {
  if (!response) return [];
  const raw =
    response?.data?.notifications
    ?? response?.data?.items
    ?? response?.data?.list
    ?? response?.notifications
    ?? response?.items
    ?? (Array.isArray(response?.data) ? response.data : null)
    ?? (Array.isArray(response) ? response : []);

  if (!Array.isArray(raw)) return [];
  return raw.map(normalizeApiNotification).filter(Boolean);
};

export const loadStoredNotifications = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const saveStoredNotifications = (list) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list.slice(0, MAX_STORED)));
  } catch {
    /* ignore quota */
  }
};

export const mergeNotifications = (existing, incoming) => {
  const map = new Map(existing.map((n) => [n.id, n]));
  incoming.forEach((n) => {
    const prev = map.get(n.id);
    map.set(n.id, prev ? { ...prev, ...n, read: prev.read && n.read } : n);
  });
  return [...map.values()].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );
};

export const formatNotificationTime = (iso) => {
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    if (diffMs < 60_000) return 'Just now';
    if (diffMs < 3_600_000) return `${Math.floor(diffMs / 60_000)}m ago`;
    if (diffMs < 86_400_000) return `${Math.floor(diffMs / 3_600_000)}h ago`;
    return d.toLocaleDateString();
  } catch {
    return '';
  }
};
